/**
 * @file ListingBadges.jsx
 * @description İlan badge'leri bileşeni (Fırsat, Yeni, Fiyat Düştü)
 *
 * API'den gelen badge bilgilerini görsel olarak gösterir.
 * Kompakt ve carousel kartlarında kullanılır.
 *
 * @module widget/components/ListingBadges
 */

import { memo } from "react";
import { Flame, TrendingDown, Sparkles, Star } from "lucide-react";

/**
 * Badge türleri ve stilleri
 */
const BADGE_CONFIG = {
  opportunity: {
    label: "Fırsat",
    icon: Flame,
    bgColor: "bg-amber-500",
    textColor: "text-white",
  },
  new: {
    label: "Yeni",
    icon: Sparkles,
    bgColor: "bg-blue-500",
    textColor: "text-white",
  },
  promotion: {
    label: "Öne Çıkan",
    icon: Star,
    bgColor: "bg-emerald-500",
    textColor: "text-white",
  },
};

/**
 * Tek bir badge bileşeni
 */
function Badge({ type, size = "sm" }) {
  const config = BADGE_CONFIG[type];
  if (!config) return null;

  const Icon = config.icon;
  const sizeClasses = size === "sm"
    ? "text-[9px] px-1.5 py-0.5 gap-0.5"
    : "text-[10px] px-2 py-0.5 gap-1";
  const iconSize = size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3";

  return (
    <span
      className={`inline-flex items-center rounded font-medium ${sizeClasses} ${config.bgColor} ${config.textColor}`}
    >
      <Icon className={iconSize} strokeWidth={2} />
      {config.label}
    </span>
  );
}

/**
 * Fiyat düşüş badge'i
 */
function PriceDownBadge({ rate, size = "sm" }) {
  if (!rate || rate <= 0) return null;

  const sizeClasses = size === "sm"
    ? "text-[9px] px-1.5 py-0.5 gap-0.5"
    : "text-[10px] px-2 py-0.5 gap-1";
  const iconSize = size === "sm" ? "h-2.5 w-2.5" : "h-3 w-3";

  return (
    <span
      className={`inline-flex items-center rounded font-medium bg-green-500 text-white ${sizeClasses}`}
    >
      <TrendingDown className={iconSize} strokeWidth={2} />
      %{Math.abs(rate).toFixed(0)}
    </span>
  );
}

/**
 * İlan Badge'leri Bileşeni
 *
 * @component
 * @param {Object} props
 * @param {string[]} [props.badges] - Badge dizisi ("opportunity", "new", "promotion")
 * @param {boolean} [props.isOpportunity] - Fırsat ilanı mı
 * @param {string|null} [props.priceTrend] - Fiyat trendi ("down" veya null)
 * @param {number|null} [props.priceChangeRate] - Fiyat değişim oranı (%)
 * @param {"sm"|"md"} [props.size] - Badge boyutu
 * @param {number} [props.maxBadges] - Gösterilecek maksimum badge sayısı
 */
function ListingBadgesInner({
  badges = [],
  isOpportunity = false,
  priceTrend = null,
  priceChangeRate = null,
  size = "sm",
  maxBadges = 2,
}) {
  const badgesToShow = [];

  // Fırsat badge'i (en öncelikli)
  if (isOpportunity || badges.includes("opportunity")) {
    badgesToShow.push({ type: "opportunity", key: "opportunity" });
  }

  // Fiyat düşüş badge'i
  if (priceTrend === "down" && priceChangeRate && priceChangeRate > 0) {
    badgesToShow.push({ type: "priceDown", key: "priceDown", rate: priceChangeRate });
  }

  // Yeni badge'i
  if (badges.includes("new") && badgesToShow.length < maxBadges) {
    badgesToShow.push({ type: "new", key: "new" });
  }

  // Öne çıkan badge'i (en düşük öncelik)
  if (badges.includes("promotion") && badgesToShow.length < maxBadges) {
    badgesToShow.push({ type: "promotion", key: "promotion" });
  }

  if (badgesToShow.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1">
      {badgesToShow.slice(0, maxBadges).map((badge) =>
        badge.type === "priceDown" ? (
          <PriceDownBadge key={badge.key} rate={badge.rate} size={size} />
        ) : (
          <Badge key={badge.key} type={badge.type} size={size} />
        )
      )}
    </div>
  );
}

export const ListingBadges = memo(ListingBadgesInner);
export default ListingBadges;
